import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import ButtonBase from '@material-ui/core/ButtonBase'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import CalendarMonthIcon from 'components/material-symbol/icons/CalendarMonth'
import useStyles from './styles'

const SCROLL_PAGE_RATIO = 0.8

const DateStrip = ({
  days,
  selectedIso,
  onSelect,
  onOpenCalendar,
  showArrows = true,
}) => {
  const classes = useStyles()
  const stripRef = useRef(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = stripRef.current
    if (!el) return
    setCanScrollPrev(el.scrollLeft > 1)
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateScrollState()
    window.addEventListener('resize', updateScrollState)
    return () => window.removeEventListener('resize', updateScrollState)
  }, [updateScrollState, days])

  // Keep the selected day in view (e.g. after jumping from the calendar).
  useEffect(() => {
    const el = stripRef.current
    const selected = el?.querySelector('[aria-pressed="true"]')
    if (!el || !selected) return
    const left = selected.offsetLeft
    const right = left + selected.offsetWidth
    if (left < el.scrollLeft) {
      el.scrollLeft = left
    } else if (right > el.scrollLeft + el.clientWidth) {
      el.scrollLeft = right - el.clientWidth
    }
    updateScrollState()
  }, [selectedIso, days, updateScrollState])

  const scrollByPage = direction => {
    const el = stripRef.current
    if (!el) return
    const left = direction * el.clientWidth * SCROLL_PAGE_RATIO
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left, behavior: 'smooth' })
    } else {
      el.scrollLeft += left
    }
  }

  return (
    <div className={classes.stripRow}>
      {showArrows && (
        <ButtonBase
          className={clsx(
            classes.roundButton,
            !canScrollPrev && classes.roundButtonDisabled,
          )}
          aria-label="Previous days"
          disabled={!canScrollPrev}
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeftIcon aria-hidden="true" />
        </ButtonBase>
      )}
      <div
        className={classes.strip}
        ref={stripRef}
        onScroll={updateScrollState}
        role="group"
        aria-label="Choose a day"
      >
        {days.map(day => {
          const isSelected = day.iso === selectedIso
          const caption = day.isClosed ? 'Closed' : `${day.openCount} open`
          return (
            <ButtonBase
              key={day.iso}
              className={clsx(
                classes.dayCard,
                isSelected && classes.dayCardSelected,
                day.isClosed && !isSelected && classes.dayCardClosed,
              )}
              disabled={day.isClosed && !isSelected}
              aria-pressed={isSelected}
              aria-label={`${day.fullLabel}, ${
                day.isClosed ? 'closed' : `${day.openCount} open`
              }`}
              onClick={() => onSelect(day)}
              data-testid="date-strip-day"
            >
              <span className={classes.dayLabel} aria-hidden="true">
                {day.isToday ? 'TODAY' : day.weekdayLabel}
              </span>
              <span className={classes.dayNumber} aria-hidden="true">
                {day.dayNumber}
              </span>
              <span className={classes.dayCaption} aria-hidden="true">
                {caption}
              </span>
            </ButtonBase>
          )
        })}
      </div>
      {showArrows && (
        <ButtonBase
          className={clsx(
            classes.roundButton,
            !canScrollNext && classes.roundButtonDisabled,
          )}
          aria-label="Next days"
          disabled={!canScrollNext}
          onClick={() => scrollByPage(1)}
        >
          <ChevronRightIcon aria-hidden="true" />
        </ButtonBase>
      )}
      <ButtonBase
        className={clsx(classes.roundButton, classes.calendarButton)}
        aria-label="Open calendar"
        aria-haspopup="dialog"
        onClick={onOpenCalendar}
      >
        <CalendarMonthIcon aria-hidden="true" />
      </ButtonBase>
    </div>
  )
}

DateStrip.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      iso: PropTypes.string.isRequired,
      isClosed: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  selectedIso: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onOpenCalendar: PropTypes.func.isRequired,
  showArrows: PropTypes.bool,
}

export default DateStrip
